import React, { useCallback, useMemo } from 'react';
import type { ActionFn, SetIsLoading, Filter } from '#/common/components/DataGrid';
import DataGrid from '#/common/components/DataGrid';
import columns from './columns';
import { actions } from './actions';
import { gql } from '@apollo/react-hooks';
import { getCapitalNeedList } from '#/graphql/queries';
import useCapitalNeedDelete from '../../hooks/useDeleteCapitalNeedMutation';
import useToast from '#/common/hooks/useToast';
import { CapitalNeed } from '#/graphql/types';
import { useSearchParams } from 'react-router-dom';
import getPermanentFilterValue from '#/common/utils/parsers/parsePermanentFilters';
import useAccessControl from '#/common/hooks/useAccessControl';

const getCapitalNeedListQuery = gql(getCapitalNeedList);

interface CapitalNeedDataGridProps {
  omitColumns?: string[];
  hideNewAction?: boolean;
  permanentFilters?: Filter[];
}

const CapitalNeedDataGrid: React.FC<CapitalNeedDataGridProps> = ({
  omitColumns,
  hideNewAction,
  permanentFilters,
}) => {
  const [searchParams] = useSearchParams();
  const assetId =
    searchParams.get('assetId') ||
    getPermanentFilterValue('assetId', permanentFilters) ||
    undefined;

  const { confirm } = useToast();
  const { deleteCapitalNeed } = useCapitalNeedDelete();

  const deleteItem = useCallback(
    async (selectedItems: CapitalNeed[], { setIsLoading }: { setIsLoading?: SetIsLoading }) => {
      if (!selectedItems || selectedItems.length === 0) {
        return undefined;
      }

      const sure = await confirm(
        'error',
        `Are you sure you want to delete ${selectedItems.length} capital need${selectedItems.length > 1 ? 's' : ''}?`,
      );
      if (!sure) {
        return;
      }

      setIsLoading?.(true, 'Deleting data...');
      const idsToDelete = selectedItems.map((item: CapitalNeed) => item.id);
      await deleteCapitalNeed(idsToDelete.join(','), assetId);
      setIsLoading?.(false);
    },
    [deleteCapitalNeed, confirm, assetId],
  );

  const { canCreateCapitalNeed, canDeleteCapitalNeed } = useAccessControl();
  const capitalNeedSelectedActions: ActionFn<CapitalNeed>[] = canDeleteCapitalNeed
    ? [
        {
          key: 'delete-capital-needs',
          icon: 'trash',
          text: 'Delete',
          disabled: (e) => !e.selectedItems || e.selectedItems.length === 0,
          onClick: async (e) => {
            await deleteItem(e.selectedItems, { setIsLoading: e.setIsLoading });
            e.dg?.instance.clearSelection();
            e.dg?.instance.refresh();
          },
        },
      ]
    : [];

  const cols = useMemo(
    () => columns.filter((c) => !(omitColumns || []).includes(c.dataField)),
    [omitColumns],
  );

  const gridActions = useMemo(
    () => (hideNewAction || !canCreateCapitalNeed ? [] : actions),
    [hideNewAction, canCreateCapitalNeed, actions],
  );

  return (
    <DataGrid
      id="capital-needs"
      pluralText="Capital Needs"
      singularText="Capital Need"
      columns={cols}
      recordUrl="/capital-needs/:id"
      actions={gridActions}
      selectedActions={capitalNeedSelectedActions}
      getItemsQuery={getCapitalNeedListQuery}
      permanentFilters={permanentFilters}
      {...(gridActions.length === 0 && { selectionMode: 'none' })}
    />
  );
};

export default React.memo(CapitalNeedDataGrid);
